import {
  KEEPER_SYNC_STARTED,
  KEEPER_SYNC_STATUS_UPDATED,
  KEEPER_SYNC_COMPLETE,
  KEEPER_SYNC_ERROR,
  KEEPER_SYNC_RESET,
  KEEPER_RECON_LOADING,
  KEEPER_RECON_REPORT_LOADED,
  KEEPER_RECON_REPORT_FAILED,
  KEEPER_RECON_EXECUTING,
  KEEPER_RECON_EXECUTED,
  KEEPER_RECON_EXECUTE_FAILED,
  KEEPER_RECON_ROW_UPDATING,
  KEEPER_RECON_ROW_UPDATED,
  KEEPER_RECON_ROW_UPDATE_FAILED,
  KEEPER_RECON_ROW_APPROVED,
  KEEPER_RECON_ROW_RESOLVED,
  KEEPER_RECON_COMPANIES_LOADED,
  KEEPER_RECON_UNMAPPED_LOADED,
  KEEPER_RECON_MAPPINGS_LOADED,
} from './keeperReconTypes'
import api from '../../services/api'
import { addErrorToast, addSaveToast } from './companyActionCreators'

// ─── Sync ────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 2000
let activePollTimer = null

const stopPolling = () => {
  if (activePollTimer) {
    clearTimeout(activePollTimer)
    activePollTimer = null
  }
}

const startPolling = (syncId, dispatch) => {
  stopPolling()

  const poll = async () => {
    try {
      const res = await api.get(`/keeper/recon/sync/status?syncId=${syncId}`)
      if (!res.ok) {
        dispatch({ type: KEEPER_SYNC_ERROR, payload: 'Failed to fetch sync status' })
        return
      }

      const data = await res.json()
      dispatch({ type: KEEPER_SYNC_STATUS_UPDATED, payload: data })

      if (data.status === 'complete') {
        dispatch({ type: KEEPER_SYNC_COMPLETE, payload: data })
        // Auto-process the sync data into report rows
        dispatch(processSyncData(syncId))
        activePollTimer = null
        return
      }

      if (data.status === 'error') {
        dispatch({ type: KEEPER_SYNC_ERROR, payload: data.error })
        activePollTimer = null
        return
      }

      activePollTimer = setTimeout(poll, POLL_INTERVAL_MS)
    } catch (e) {
      dispatch({ type: KEEPER_SYNC_ERROR, payload: 'Network error polling status' })
      activePollTimer = null
    }
  }

  poll()
}

export const startKeeperSync = (email, password) => async (dispatch) => {
  try {
    const res = await api.post('/keeper/recon/sync', { email, password: password || null })
    if (!res.ok) {
      const error = await res.text()
      dispatch({ type: KEEPER_SYNC_ERROR, payload: error || 'Failed to start sync' })
      return
    }

    const data = await res.json()
    dispatch({ type: KEEPER_SYNC_STARTED, payload: data })

    if (data.status !== 'error' && data.status !== 'awaiting_sso') {
      startPolling(data.syncId, dispatch)
    }
  } catch (e) {
    dispatch(addErrorToast(e))
    dispatch({ type: KEEPER_SYNC_ERROR, payload: 'Network error starting sync' })
  }
}

export const submit2faCode = (syncId, code) => async (dispatch) => {
  try {
    const res = await api.post('/keeper/recon/sync/2fa', { syncId, code })
    if (!res.ok) {
      const error = await res.text()
      dispatch({ type: KEEPER_SYNC_ERROR, payload: error || 'Failed to submit 2FA code' })
      return
    }

    const data = await res.json()
    dispatch({ type: KEEPER_SYNC_STATUS_UPDATED, payload: data })
    startPolling(syncId, dispatch)
  } catch (e) {
    dispatch(addErrorToast(e))
    dispatch({ type: KEEPER_SYNC_ERROR, payload: 'Network error submitting 2FA' })
  }
}

export const submitSsoToken = (syncId, token) => async (dispatch) => {
  try {
    const res = await api.post('/keeper/recon/sync/sso', { syncId, token })
    if (!res.ok) {
      const error = await res.text()
      dispatch({ type: KEEPER_SYNC_ERROR, payload: error || 'Failed to submit SSO token' })
      return
    }

    const data = await res.json()
    dispatch({ type: KEEPER_SYNC_STATUS_UPDATED, payload: data })
    startPolling(syncId, dispatch)
  } catch (e) {
    dispatch(addErrorToast(e))
    dispatch({ type: KEEPER_SYNC_ERROR, payload: 'Network error submitting SSO token' })
  }
}

export const resetKeeperSync = () => {
  stopPolling()
  return { type: KEEPER_SYNC_RESET }
}

const processSyncData = (syncId) => async (dispatch) => {
  try {
    const res = await api.post(`/keeper/recon/sync/process?syncId=${syncId}`)
    if (res.ok) {
      dispatch(addSaveToast('Keeper data processed into report'))
      dispatch(fetchReport())
    }
  } catch (e) {
    // Non-critical — report can be generated on next sync
  }
}

// ─── Dashboard ───────────────────────────────────────────────────────

export const fetchReport = () => async (dispatch) => {
  try {
    dispatch({ type: KEEPER_RECON_LOADING })
    const res = await api.get('/keeper/recon/report')
    if (!res.ok) throw new Error(`HTTP ${res.status}`)

    const data = await res.json()
    dispatch({ type: KEEPER_RECON_REPORT_LOADED, payload: data })
  } catch (err) {
    dispatch({ type: KEEPER_RECON_REPORT_FAILED })
    dispatch(addErrorToast('Failed to load Keeper report'))
  }
}

export const executeRecon = () => async (dispatch) => {
  try {
    dispatch({ type: KEEPER_RECON_EXECUTING })
    const res = await api.post('/keeper/recon/execute')
    if (!res.ok) throw new Error(`HTTP ${res.status}`)

    const data = await res.json()
    dispatch({ type: KEEPER_RECON_EXECUTED, payload: data })
    dispatch(addSaveToast(`Auto-recon complete: ${data.companiesReconciled} product(s) reconciled`))
    dispatch(fetchReport())
  } catch (err) {
    dispatch({ type: KEEPER_RECON_EXECUTE_FAILED })
    dispatch(addErrorToast('Failed to execute auto-recon'))
  }
}

export const approveCompany = (companyId) => async (dispatch) => {
  try {
    dispatch({ type: KEEPER_RECON_ROW_UPDATING, payload: companyId })
    const res = await api.post(`/keeper/recon/${companyId}/approve`)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)

    dispatch({ type: KEEPER_RECON_ROW_APPROVED, payload: companyId })
    dispatch(addSaveToast('Company approved'))
  } catch (err) {
    dispatch({ type: KEEPER_RECON_ROW_UPDATE_FAILED })
    dispatch(addErrorToast('Failed to approve company'))
  }
}

export const resolveCompany = (companyId) => async (dispatch) => {
  try {
    dispatch({ type: KEEPER_RECON_ROW_UPDATING, payload: companyId })
    const res = await api.post(`/keeper/recon/${companyId}/resolve`)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)

    const data = await res.json()

    if (data.warnings && data.warnings.length > 0) {
      data.warnings.forEach((w) => dispatch(addErrorToast(w)))
      dispatch({ type: KEEPER_RECON_ROW_UPDATE_FAILED })
    } else {
      dispatch({ type: KEEPER_RECON_ROW_RESOLVED, payload: companyId })
      dispatch(addSaveToast('Company resolved successfully'))
    }
  } catch (err) {
    dispatch({ type: KEEPER_RECON_ROW_UPDATE_FAILED })
    dispatch(addErrorToast('Failed to resolve company'))
  }
}

// ─── Company Mappings ────────────────────────────────────────────────

export const fetchCompanyLabels = () => async (dispatch) => {
  try {
    const res = await api.get('/keeper/recon/companies')
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    dispatch({ type: KEEPER_RECON_COMPANIES_LOADED, payload: data })
  } catch (err) {
    dispatch(addErrorToast('Failed to load company labels'))
  }
}

export const fetchMappings = () => async (dispatch) => {
  try {
    const res = await api.get('/keeper/recon/company-mapping')
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    dispatch({ type: KEEPER_RECON_MAPPINGS_LOADED, payload: data })
  } catch (err) {
    // Silent — mappings are supplementary
  }
}

export const fetchUnmappedCompanies = () => async (dispatch) => {
  try {
    const res = await api.get('/keeper/recon/unmapped-companies')
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    dispatch({ type: KEEPER_RECON_UNMAPPED_LOADED, payload: data })
  } catch (err) {
    dispatch(addErrorToast('Failed to load unmapped companies'))
  }
}

export const saveCompanyMapping = (keeperEnterpriseId, keeperEnterpriseName, companyId, companyType) => async (dispatch) => {
  try {
    const res = await api.post('/keeper/recon/company-mapping', {
      keeperEnterpriseId,
      keeperEnterpriseName,
      companyId,
      companyType,
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)

    dispatch(addSaveToast('Company mapping saved'))
    dispatch(fetchReport())
    dispatch(fetchMappings())
    dispatch(fetchUnmappedCompanies())
  } catch (err) {
    dispatch(addErrorToast('Failed to save company mapping'))
  }
}

export const deleteCompanyMapping = (keeperEnterpriseId) => async (dispatch) => {
  try {
    const res = await api.deleteCall(`/keeper/recon/company-mapping/${keeperEnterpriseId}`)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)

    dispatch(addSaveToast('Company mapping deleted'))
    dispatch(fetchReport())
    dispatch(fetchMappings())
    dispatch(fetchUnmappedCompanies())
  } catch (err) {
    dispatch(addErrorToast('Failed to delete company mapping'))
  }
}
